/**
 * Resource functions — one async call per logical endpoint. Today they return
 * the existing mock fixtures (USE_MOCKS); the false branch hits the real API at
 * the same shape. Shapes here are the CURRENT fixture shapes so a page can move
 * from `import { const }` to `useX()` with no render change; the separate
 * contract-shape migration (minor-unit money, ISO dates, renamed fields per
 * src/contracts) is tracked in docs/API_CONTRACT.md.
 */
import { USE_MOCKS, mockDelay, apiGet, apiGetList } from './client';
import {
  kpisByBusiness, recentActivity, allBusinesses, currentBusiness,
  invoicesByBusiness, allInvoices, subscriptionsByBusiness,
  type Kpis, type Activity, type Business, type Invoice, type Subscription,
} from '../mocks/data';

export type DashboardData = { kpis: Kpis; activity: Activity[] };

export async function getDashboard(businessId: string): Promise<DashboardData> {
  if (!USE_MOCKS) return apiGet<DashboardData>(`/businesses/${businessId}/dashboard`);
  const kpis = kpisByBusiness[businessId] ?? kpisByBusiness[currentBusiness.id];
  if (!kpis) throw new Error(`no dashboard data for ${businessId}`);
  return mockDelay({ kpis, activity: recentActivity });
}

export async function listBusinesses(): Promise<Business[]> {
  if (!USE_MOCKS) return apiGetList<Business>('/businesses');
  return mockDelay(allBusinesses);
}

export async function listInvoices(businessId?: string): Promise<Invoice[]> {
  if (!USE_MOCKS) {
    return apiGetList<Invoice>(businessId ? `/businesses/${businessId}/invoices` : '/invoices');
  }
  if (!businessId) return mockDelay(allInvoices);
  return mockDelay(invoicesByBusiness[businessId] ?? []);
}

export async function getSubscription(businessId: string): Promise<Subscription> {
  if (!USE_MOCKS) return apiGet<Subscription>(`/businesses/${businessId}/subscription`);
  const sub = subscriptionsByBusiness[businessId];
  if (!sub) throw new Error(`no subscription for ${businessId}`);
  return mockDelay(sub);
}

export async function getSubscriptionsMap(): Promise<Record<string, Subscription>> {
  if (!USE_MOCKS) return apiGet<Record<string, Subscription>>('/subscriptions');
  return mockDelay(subscriptionsByBusiness);
}

export type TeamMember = { id: string; name: string; email: string; role: string; status: string; joinedAt: string | null };

export async function getTeam(businessId: string): Promise<TeamMember[]> {
  if (!USE_MOCKS) return apiGetList<TeamMember>(`/businesses/${businessId}/team`);
  return mockDelay([]);
}

export type AddOnsData = {
  active: Array<{ id: string; name: string; unitPriceMinor: number; qty: number; oneTime: boolean }>;
  available: Array<{ id: string; name: string; unitPriceMinor: number; description: string; oneTime: boolean }>;
};

export async function getAddOns(businessId: string): Promise<AddOnsData> {
  if (!USE_MOCKS) return apiGet<AddOnsData>(`/businesses/${businessId}/add-ons`);
  return mockDelay({ active: [], available: [] });
}

/** Staff (POS-only) landing context — see GET /staff/context. */
export type StaffContext = {
  staffName: string;
  roleLabel: string;
  /** publicId — passed to the POS SSO handoff (openPos). */
  businessId: string;
  businessName: string;
  /** POS subdomain handle; the host is `<slug>.<brand.domain>`. */
  slug: string;
  adminName: string | null;
};

export async function getStaffContext(): Promise<StaffContext> {
  if (!USE_MOCKS) return apiGet<StaffContext>('/staff/context');
  return mockDelay({
    staffName: 'Rina Akter',
    roleLabel: 'Cashier',
    businessId: 'biz_khan',
    businessName: 'Khan Bakery',
    slug: 'khan-bakery',
    adminName: 'Imran Khan',
  });
}
